import mapboxglSupported from '@mapbox/mapbox-gl-supported';
import { syncFavorite } from '../helpers/favorites';
import { initViewHeight, initViewHeightOnResize } from '../helpers/view-height';
import seoInformation from '../SeoInformation/SeoInformation';
import Funnel from '../Funnel/Funnel';
import Popup from '../UiKit/Elements/Popup/Popup';
import { initAppSubscribeModal } from '../AppSubscribeModal/AppSubscribeModal';
import { sendMapboxSupportedEvent } from '../helpers/mapbox-supported';
import { loadChunk } from '../helpers/load-chunk';
import { initNavigationCitySelect } from '../Navigation/NavigationCitySelect/NavigationCitySelect';
import '../Building/BuildingIncomeProperty/BuildingIncomeProperty';
import '../SubscriptionBlock/SubscriptionBlock';
import '../RegionSubscribe/RegionSubscribe';
import './SearchQuestionary/SearchQuestionary';
import './Search.scss';
import { initCardGalleries } from '../CardGallery/CardGallery';
import { initDevelopers } from './SearchDevelopers/SearchDevelopers';
import { sendRealtyTypeClickEvents } from '../Funnel/FunnelFilters/sendRealtyTypeEvents';

const MOBILE_WIDTH = 725;

class Search {
  constructor() {
    this.root = document.querySelector('#search-bar');
    this.realtyTypeChips = this.root.querySelectorAll('[data-chip-realty-type]');
    this.initFunnel();
    this.initMap();
    this.initSubscriptionPopup();
    this.attachListeners();
    this.initStickySearchBar();

    initNavigationCitySelect('search-bar-city-select');
    seoInformation();
    initDevelopers();
  }

  initFunnel = () => {
    this.funnel = new Funnel({
      endpoint: '/api/filter/catalog',
      geoUrl: window.params.geoUrl,
      onResponse: (json) => {
        if (!json.template) {
          throw new Error('Response without template');
        }
        document.getElementById('search-results').innerHTML = json.template;
        document.getElementById('seo-information').innerHTML = json.seo_information;
        window.initCountdown();
        this.existingPopup?.close();
        this.createSortingPopup();
        syncFavorite();
        seoInformation();
        initCardGalleries();
        initDevelopers();
        initAppSubscribeModal();
        this.initSubscriptionPopup();
        this.initCardHover();
      },
      onFetch: (params, fitBounds) => {
        if (!this.searchMap) return;
        this.updateMapData(params, fitBounds);
      },
    });

    this.funnel.setQuery(this.funnel.query, {
      triggerReplaceUrl: false,
      withRequest: false,
    });
  };

  updateMapData(params, fitBounds) {
    this.searchMap.fetchMapPrices(params);
    this.searchMap.fetchFilterMap(params, fitBounds);
  }

  initCardHover() {
    const cards = [...document.querySelectorAll('[data-card-location]')];

    if (this.pin) {
      this.pin.remove();
    }

    cards.forEach((card) => {
      const location = card.getAttribute('data-card-location').split('|');
      card.addEventListener('mouseenter', () => {
        if (!this.searchMap) return;
        if (this.pin) {
          this.pin.remove();
        }
        this.pin = this.searchMap.drawPin(location);
      });
      card.addEventListener('mouseleave', () => {
        if (!this.pin) return;
        this.pin.remove();
      });
    });
  }

  createSortingPopup = () => {
    this.existingPopup = new Popup('sorting', {
      disableTriggerClassNameChange: true,
      placement: 'top',
    });
  };

  attachListeners = () => {
    document.addEventListener('click', (event) => {
      [].forEach.call(document.querySelectorAll('[data-order]'), (node) => {
        if (node.contains(event.target)) {
          event.preventDefault();
          const order = node.getAttribute('data-order');
          const sortingContainer = node.closest('[data-switcher="sorting"]');
          if (sortingContainer) {
            sortingContainer
              .querySelectorAll('[data-order]')
              .forEach((n) => n.classList.remove('-active'));
            node.classList.add('-active');
          } else {
            const sortingPopup = document.querySelector('[data-uipopup-for="sorting"]');
            if (sortingPopup) {
              sortingPopup
                .querySelectorAll('[data-order]')
                .forEach((n) => n.classList.remove('-selected'));
            }
            node.classList.add('-selected');
            const sortingText = document.querySelector('[data-sorting-content]');
            if (sortingText) {
              sortingText.textContent = node.getAttribute('data-content');
            }
          }
          this.funnel.assign('order', order);
        }
      });

      [].forEach.call(document.querySelectorAll('[data-page]'), (node) => {
        if (node.contains(event.target)) {
          event.preventDefault();
          const pageNumber = Number(node.getAttribute('data-page'));
          this.funnel.assign('page', pageNumber);
          try {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } catch (err) {
            window.scrollTo(0, 0);
          }
        }
      });
    });

    this.createSortingPopup();
    initViewHeight();
    initViewHeightOnResize();

    this.realtyTypeChips.forEach((chip) => {
      chip.addEventListener('click', () => {
        sendRealtyTypeClickEvents(chip.getAttribute('data-chip-realty-type'));
      });
    });
  };

  initMap = async () => {
    if (window.isMobile && window.innerWidth <= MOBILE_WIDTH) return;
    sendMapboxSupportedEvent();
    if (mapboxglSupported.supported()) {
      try {
        const module = await loadChunk(() =>
          import(/* webpackChunkName: "SearchMap" */ './SearchMap/SearchMap'),
        );
        const SearchMap = module.default;

        this.searchMap = new SearchMap(this.funnel);
        this.initCardHover();
        if (!this.searchMap.filteredPrices) {
          this.updateMapData(this.funnel.requestBody);
        }
      } catch (error) {
        console.warn(error);
      }
    } else {
      document.querySelector('#notification').classList.add('-active');
    }
  };

  initSubscriptionPopup = () => {
    const wrapper = document.getElementById('search-results');
    if (!wrapper) return;
    const triggers = wrapper.querySelectorAll('[data-subscription-id]');
    Array.from(triggers).forEach((trigger) => {
      new Popup(trigger.dataset.subscriptionId);
    });
  };

  initStickySearchBar = () => {
    const DEAD_ZONE = 10;
    const searchBar = this.root;
    const navigation = document.querySelector('#navigation');
    const navHeight = navigation ? navigation.offsetHeight : 0;

    if (!searchBar) return;

    let lastScrollY = window.scrollY;
    const heightSumm = searchBar.offsetHeight + navHeight;
    const hiddenClass = '-hidden';
    const fixedClass = '-fixed';

    function onScroll() {
      const currentScrollY = window.scrollY;
      const diff = Math.abs(currentScrollY - lastScrollY);
      const headerScrolled = currentScrollY > heightSumm;
      if (diff < DEAD_ZONE) return;

      if (!headerScrolled) {
        searchBar.classList.remove(fixedClass);
        searchBar.classList.remove(hiddenClass);
        lastScrollY = currentScrollY;
      }

      if (currentScrollY > lastScrollY && headerScrolled) {
        // Скрол вниз
        searchBar.classList.add(hiddenClass);
        searchBar.classList.remove(fixedClass);
      } else if (currentScrollY < lastScrollY && headerScrolled) {
        searchBar.classList.remove(hiddenClass);
        searchBar.classList.add(fixedClass);
      }

      lastScrollY = currentScrollY;
    }

    window.addEventListener('scroll', onScroll);
  };
}

new Search();
